import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Log } from 'ng2-logger';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StrainMeasurementImage } from '@app/strain/common/models/view-measurement-models/strain-measurement.image';
import { StrainGageConfigurationModalComponent } from '../strain-gage-configuration-modal/strain-gage-configuration-modal.component';
import { DomSanitizer } from '@angular/platform-browser';
import { Gage } from "@app/strain/config/gages/models/gage.model";
import { RegistryItemSampleBoard } from '@app/strain/materials/models/registry-item-sample-board';
import { SampleBoardMeasurementInstructions } from '@app/strain/sample-boards/models/documentation/sample-board-measurement-instructions.model';
import {
    StrainSampleBoardLoadModalComponent
} from '../strain-sample-board-load-modal/strain-sample-board-load-modal.component';

@Component({
    selector: 'app-strain-measurement-gage-details-form',
    templateUrl: './strain-measurement-gage-details-form.component.html',
    styleUrls: ['./strain-measurement-gage-details-form.component.scss']
})
export class StrainMeasurementGageDetailsFormComponent implements OnInit, OnChanges {
    _logger = Log.create(StrainMeasurementGageDetailsFormComponent.name);

    @ViewChild(StrainGageConfigurationModalComponent, { static: true }) gageConfigurationModal: StrainGageConfigurationModalComponent;
    @ViewChild(StrainSampleBoardLoadModalComponent, { static: true }) sampleBoardModal: StrainSampleBoardLoadModalComponent;

    @Input() hasMultiple = true;
    @Input() showValidations = false;
    @Input() isLoading = false;
    @Input() isLoadingSampleBoardInstructions = false;
    @Input() isEditMode: boolean;
    @Input() measurementImages: StrainMeasurementImage[];
    @Input() gages: Gage[];
    @Input() sampleBoards: RegistryItemSampleBoard[] = [];
    @Output() loadSampleBoardInstructions = new EventEmitter<{ index: number; sampleBoardId: number }>();

    public form: UntypedFormGroup;

    selectedIndex: number;

    constructor(private fb: UntypedFormBuilder,
                private sanitizer: DomSanitizer) {
    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Preparing strain configuration form");

        this.initForm(this.isEditMode);
    }

    initForm(isEditMode) {
        this.showValidations = false;
        this.form = this.fb.group({
            id: [null],
            entries: this.fb.array([], Validators.required)
        });

        if (isEditMode && this.measurementImages && this.measurementImages.length > 0) {
            this.measurementImages.forEach(item => {
                this.array('entries').push(this.buildRow(item))
            });
        } else {
            this.addItem();
        }
    }

    control(name: string): UntypedFormControl {
        return this.form.get(name) as UntypedFormControl;
    }

    array(name: string) {
        return this.form.get(name) as UntypedFormArray;
    }

    arrayControl(index, name) {
        return (this.array('entries').controls[index] as UntypedFormArray).get(name) as UntypedFormControl;
    }

    addItem() {
        this.array('entries').push(this.buildRow());
        this.showValidations = false;
    }

    removeItem(index) {
        this.array('entries').removeAt(index);
    }

    buildRow(item = null): UntypedFormGroup {
        if (!item) {
            return this.fb.group({
                observations: [null, Validators.required],
                frontSideImage: [null],
                backSideImage: [null],
                gages: this.fb.array([], Validators.required)
            });
        } else {
            return this.fb.group({
                observations: [item.observations, Validators.required],
                frontSideImage: [item.frontSideImage],
                backSideImage: [item.backSideImage],
                gages: this.buildGagesFormArray(item.gages || [])
            });
        }
    }

    buildGagesFormArray(gages: any[]) {
        return this.fb.array(gages.map(gage => this.buildGageRow(gage)), Validators.required);
    }

    buildGageRow(gage) {
        return this.fb.group({
            name: [gage.name, Validators.required],
            gage: [gage.gage, Validators.required],
            position: [gage.position, Validators.required],
        });
    }

    value() {
        return this.form.value;
    }

    valid() {
        return this.form && this.form.valid;
    }

    reset() {
        this._logger.info("resetForm:: Resetting line details form");
        this.form.reset();
    }


    ngOnChanges(changes: SimpleChanges): void {
    }

    onDuplicate(index: any) {
        this.array('entries').push(this.buildRow(this.array('entries').controls[index].value));
    }

    onEditClicked(index) {
        this.selectedIndex = index;
        this.gageConfigurationModal.open(this.array('entries').controls[index].value);
    }

    onLoadFromSampleBoardClicked(index: number) {
        if (!this.hasSampleBoards) {
            return;
        }

        this.selectedIndex = index;
        this.sampleBoardModal?.open();
    }

    onSampleBoardLoadRequested(sampleBoardId: number) {
        if (!sampleBoardId || this.selectedIndex === null || this.selectedIndex === undefined) {
            return;
        }

        this.loadSampleBoardInstructions.emit({
            index: this.selectedIndex,
            sampleBoardId
        });
    }

    closeSampleBoardModal() {
        this.sampleBoardModal?.close();
    }

    getGages(index) {
        return this.arrayControl(index, 'gages').value;
    }

    onGageSave(entry: StrainMeasurementImage) {
        this.array('entries').controls[this.selectedIndex].patchValue({
            frontSideImage: entry.frontSideImage,
            backSideImage: entry.backSideImage,
        });

        const gagesFormArray = this.array('entries').controls[this.selectedIndex].get('gages') as UntypedFormArray;
        while (gagesFormArray.length > 0) {
            gagesFormArray.removeAt(0);
        }
        entry.gages.forEach(gage => gagesFormArray.push(this.buildGageRow(gage)));
        this.selectedIndex = null;
    }

    getImageUrl(index: number, back = false) {
        const image = this.arrayControl(index, back ? 'backSideImage' : 'frontSideImage').value
        if (image && image.url) {
            return this.sanitizer.bypassSecurityTrustUrl(image.url);
        }
        return null;
    }

    get hasSampleBoards() {
        return (this.sampleBoards || []).some(item => !!item?.sampleBoard?.id);
    }

    applySampleBoardInstructions(index: number, instructions: SampleBoardMeasurementInstructions) {
        const entryControl = this.array('entries').controls[index] as UntypedFormGroup;
        entryControl.patchValue({
            observations: instructions?.observations || null,
            frontSideImage: instructions?.frontSideImage || null,
            backSideImage: instructions?.backSideImage || null
        });

        const gagesFormArray = entryControl.get('gages') as UntypedFormArray;
        while (gagesFormArray.length > 0) {
            gagesFormArray.removeAt(0);
        }
        (instructions?.gages || []).forEach(gage => gagesFormArray.push(this.buildGageRow(gage)));
    }
}
